import { ReactNode, createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

export interface ActionSheetStackContextValue {
  closeAll: () => void;
  isClosingAll: boolean;
  isFirst: (id: string) => boolean;
  isTop: (id: string) => boolean;
  push: (id: string) => void;
  remove: (id: string) => void;
  stack: string[];
}

const ActionSheetStackContext = createContext<ActionSheetStackContextValue | null>(null);

interface ActionSheetStackProps {
  children: ReactNode;
  onCloseAll?: () => void;
}

const ActionSheetStack = ({ children, onCloseAll }: ActionSheetStackProps): ReactNode => {
  const [stack, setStack] = useState<string[]>([]);
  const closingAllRef = useRef(false);
  const [isClosingAll, setIsClosingAll] = useState(false);

  const push = useCallback((id: string) => {
    closingAllRef.current = false;
    setIsClosingAll(false);
    setStack((prev) => {
      if (prev.includes(id)) return prev;
      return [...prev, id];
    });
  }, []);

  const remove = useCallback((id: string) => {
    setStack((prev) => prev.filter((item) => item !== id));
  }, []);

  const isTop = useCallback(
    (id: string) => {
      return stack.length === 0 || stack[stack.length - 1] === id;
    },
    [stack]
  );

  const isFirst = useCallback(
    (id: string) => {
      return stack.length === 0 || stack[0] === id;
    },
    [stack]
  );

  const closeAll = useCallback(() => {
    closingAllRef.current = true;
    setIsClosingAll(true);
    setStack([]);
    onCloseAll?.();
  }, [onCloseAll]);

  const value = useMemo(
    () => ({ closeAll, isClosingAll, isFirst, isTop, push, remove, stack }),
    [closeAll, isClosingAll, isFirst, isTop, push, remove, stack]
  );

  return <ActionSheetStackContext.Provider value={value}>{children}</ActionSheetStackContext.Provider>;
};

function useActionSheetStack(): ActionSheetStackContextValue {
  const context = useContext(ActionSheetStackContext);
  if (!context) {
    throw new Error("useActionSheetStack must be used within an ActionSheetStack");
  }
  return context;
}

function useActionSheetStackContext(): ActionSheetStackContextValue | null {
  return useContext(ActionSheetStackContext);
}

export { ActionSheetStack, useActionSheetStack, useActionSheetStackContext };
